import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Checkbox } from '@fluentui/react-components'
import { ResizableBox } from 'react-resizable'
import 'react-resizable/css/styles.css'
import './StyleSheet.css'

/**
 * FileSelectionAccordion Component
 *
 * A collapsible and resizable UI component that displays a list of uploaded files.
 * It allows users to select individual files or all files at once via checkboxes.
 * This component is typically rendered within the Footer when 'file_upload' is active.
 *
 * @param {} files - List of file objects { name, isSelected }
 * @param {function} onToggleFileSelection - Handler to toggle a specific file
 * @param {function} onSelectAll - Handler to toggle all files
 * @param {boolean} isLoading - Disables interaction during loading states
 * @param {boolean} isUploading - Disables interaction during upload states
 */
const FileSelectionAccordion = ({
  files,
  onToggleFileSelection,
  onSelectAll,
  isLoading,
  isUploading
}) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [height, setHeight] = useState(105)
  const areAllFilesSelected =
    files.length > 0 && files.every(file => file.isSelected)
  const selectedFilesCount = files.filter(file => file.isSelected).length
  const isDisabled = isLoading || isUploading

  /**
   * Handles the "Select All" checkbox change event.
   * Passes the new checked state to the parent handler.
   * @param {Object} e - Event object
   * @param {Object} data - Checkbox data containing 'checked' state
   */
  const handleSelectAllChange = (e, data) => {
    onSelectAll(data.checked)
  }

  /**
   * Toggles the accordion's visibility (expanded/collapsed).
   * Prevents toggling if the app is currently loading data.
   */
  const toggleExpansion = () => {
    setIsExpanded(!isExpanded)
  }

  /**
   * Callback for the ResizableBox component.
   * Updates the internal height state as the user drags the resize handle.
   * @param {Object} event - The mouse event
   * @param {Object} data - Contains element, size {width, height}, and handle info
   */
  const onResize = (event, { element, size, handle }) => {
    setHeight(size.height)
  }

  return (
    <div className='file-accordion'>
      <div className='accordion-header' onClick={toggleExpansion}>
        <div className='accordion-title'>
          Selected files ({selectedFilesCount}/{files.length} selected)
        </div>
        {/* This is the expand/collapse arrow */}
        <div className={`collapse-icon ${isExpanded ? 'expanded' : ''}`}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            fill='currentColor'
            viewBox='0 0 16 16'
          >
            <path
              fillRule='evenodd'
              d='M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z'
            />
          </svg>
        </div>
      </div>

      {/* The content is only visible when expanded */}
      {isExpanded &&
        <ResizableBox
          width={Infinity}
          height={height}
          onResize={onResize}
          resizeHandles={['n']}
          minConstraints={[Infinity, 100]}
          maxConstraints={[Infinity, 500]}
          className='resizable-accordion-content'
        >
          <div className='accordion-content'>
            <div className='select-all-container-inline'>
              <Checkbox
                label={
                  <span style={{ fontSize: '11px' }}>Select all files</span>
                }
                checked={areAllFilesSelected}
                onChange={handleSelectAllChange}
                disabled={isDisabled}
              />
            </div>
            <ul className='attached-files-ul-inline'>
              {files.map(file =>
                <li key={file.name} className='file-accordion-item'>
                  <label className='file-list-item-simple with-checkbox'>
                    <Checkbox
                      checked={file.isSelected || false}
                      onChange={() => onToggleFileSelection(file.name)}
                      disabled={isDisabled}
                    />
                    <span className='file-name' title={file.name}>
                      {file.name}
                    </span>
                  </label>
                </li>
              )}
            </ul>
          </div>
        </ResizableBox>}
    </div>
  )
}

FileSelectionAccordion.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      isSelected: PropTypes.bool
    })
  ).isRequired,
  onToggleFileSelection: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isUploading: PropTypes.bool
}

FileSelectionAccordion.defaultProps = {
  isLoading: false,
  isUploading: false
}

export default FileSelectionAccordion
